import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import SprintRaceBox from './SprintRaceBox';
import FeatureRaceBox from './FeatureRaceBox';

function PredictionForm2026() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // State
  const [races, setRaces] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedRaceId, setSelectedRaceId] = useState('');
  const [selectedRace, setSelectedRace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Prediction state
  const [predictions, setPredictions] = useState({
    sprintPredictions: {
      podium: ['', '', '', '', ''],
      fastestLap: '',
      polePosition: '',
      firstRetirement: '',
      mostPlacesGained: '',
      driverHeadToHead: { driver1: '', driver2: '', winner: '' },
      teamHeadToHead: { team1: '', team2: '', winner: '' }
    },
    featurePredictions: {
      podium: ['', '', '', '', ''],
      fastestLap: '',
      polePosition: '',
      firstRetirement: '',
      mostPlacesGained: '',
      driverHeadToHead: { driver1: '', driver2: '', winner: '' },
      teamHeadToHead: { team1: '', team2: '', winner: '' }
    }
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Load races, drivers, teams on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Get auth token from localStorage
        const token = localStorage.getItem('authToken');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

        // Load races and filter for upcoming only
        const raceRes = await fetch('/api/f1data/races/all?season=2026', { headers });
        if (!raceRes.ok) throw new Error(`Failed to load races (${raceRes.status})`);
        const raceData = await raceRes.json();
        
        // Filter to only upcoming races (date > now)
        const now = new Date();
        const upcomingRaces = raceData.filter(race => new Date(race.date) > now);
        setRaces(upcomingRaces);

        // Load drivers for 2026 season only
        const driverRes = await fetch('/api/f1data/drivers?season=2026', { headers });
        if (!driverRes.ok) throw new Error(`Failed to load drivers (${driverRes.status})`);
        setDrivers(await driverRes.json());

        // Load teams for 2026 season only
        const teamRes = await fetch('/api/f1data/teams?season=2026', { headers });
        if (!teamRes.ok) throw new Error(`Failed to load teams (${teamRes.status})`);
        setTeams(await teamRes.json());

        setError('');
      } catch (err) {
        setError('Failed to load race data: ' + err.message);
        console.error('Load data error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Auto-select the next open race on first load
  useEffect(() => {
    if (races.length > 0 && !selectedRaceId) {
      const now = new Date();
      const nextRace = races.find(race => new Date(race.date) > now);
      if (nextRace) {
        setSelectedRaceId(nextRace._id);
        setSelectedRace(nextRace);
      }
    }
  }, [races]);

  // Load existing predictions when race is selected
  useEffect(() => {
    const loadPredictions = async () => {
      if (!selectedRaceId) {
        resetPredictions();
        return;
      }

      try {
        const token = localStorage.getItem('authToken');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

        const res = await fetch(`/api/predictions/${selectedRaceId}?season=2026`, { headers });
        if (!res.ok) {
          // No existing prediction
          resetPredictions();
          return;
        }

        const data = await res.json();
        if (data.prediction) {
          setPredictions({
            sprintPredictions: data.prediction.sprintPredictions || {
              podium: ['', '', '', '', ''],
              fastestLap: '',
              polePosition: '',
              firstRetirement: '',
              mostPlacesGained: '',
              driverHeadToHead: { driver1: '', driver2: '', winner: '' },
              teamHeadToHead: { team1: '', team2: '', winner: '' }
            },
            featurePredictions: data.prediction.featurePredictions || {
              podium: ['', '', '', '', ''],
              fastestLap: '',
              polePosition: '',
              firstRetirement: '',
              mostPlacesGained: '',
              driverHeadToHead: { driver1: '', driver2: '', winner: '' },
              teamHeadToHead: { team1: '', team2: '', winner: '' }
            }
          });
        } else {
          resetPredictions();
        }
      } catch (err) {
        console.error('Error loading predictions:', err);
        resetPredictions();
      }
    };

    loadPredictions();
  }, [selectedRaceId]);

  const resetPredictions = () => {
    setPredictions({
      sprintPredictions: {
        podium: ['', '', '', '', ''],
        fastestLap: '',
        polePosition: '',
        firstRetirement: '',
        mostPlacesGained: '',
        driverHeadToHead: { driver1: '', driver2: '', winner: '' },
        teamHeadToHead: { team1: '', team2: '', winner: '' }
      },
      featurePredictions: {
        podium: ['', '', '', '', ''],
        fastestLap: '',
        polePosition: '',
        firstRetirement: '',
        mostPlacesGained: '',
        driverHeadToHead: { driver1: '', driver2: '', winner: '' },
        teamHeadToHead: { team1: '', team2: '', winner: '' }
      }
    });
  };

  const handleRaceChange = (e) => {
    const raceId = e.target.value;
    setSelectedRaceId(raceId);

    const race = races.find(r => r._id === raceId);
    setSelectedRace(race);
    setSuccess('');
    setError('');
  };

  const handleDriverChange = (raceType, field, index, value) => {
    const key = raceType === 'sprint' ? 'sprintPredictions' : 'featurePredictions';
    const newPredictions = { ...predictions };

    if (field === 'podium') {
      newPredictions[key].podium[index] = value;
    } else if (field === 'driverHeadToHead' || field === 'teamHeadToHead') {
      newPredictions[key][field][index] = value;
    } else {
      newPredictions[key][field] = value;
    }

    setPredictions(newPredictions);
  };

  const handleSubmit = async (raceType, predictionsToSubmit) => {
    if (!selectedRaceId) {
      setError('Please select a race');
      return;
    }

    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('authToken');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      const payload = {
        season: '2026',
        [raceType + 'Predictions']: predictionsToSubmit[raceType + 'Predictions']
      };

      const res = await fetch(`/api/predictions/${selectedRaceId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to submit predictions');
      }

      const raceTypeName = raceType === 'sprint' ? 'Sprint' : 'Feature';
      setSuccess(`${raceTypeName} predictions submitted successfully!`);

      // Reload predictions
      const reloadRes = await fetch(`/api/predictions/${selectedRaceId}?season=2026`, { headers });
      if (reloadRes.ok) {
        const data = await reloadRes.json();
        if (data.prediction) {
          setPredictions({
            sprintPredictions: data.prediction.sprintPredictions || predictions.sprintPredictions,
            featurePredictions: data.prediction.featurePredictions || predictions.featurePredictions
          });
        }
      }
    } catch (err) {
      setError(`Failed to submit ${raceType} predictions: ` + err.message);
      console.error('Submit error:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="text-gray-400 text-center">Loading race data...</div>
      </div>
    );
  }

  if (error && !selectedRaceId) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  const isSprint = selectedRace?.isSprint || false;

  return (
    <div>
      {/* Error/Success Messages */}
      {error && selectedRaceId && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
          {success}
        </div>
      )}

      {/* Race Selection */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Select Race</h3>
        <select
          value={selectedRaceId}
          onChange={handleRaceChange}
          className="w-full px-4 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-red-500"
        >
          <option value="">Choose a race...</option>
          {races.map(race => (
            <option key={race._id} value={race._id}>
              {race.raceName} - {new Date(race.date).toLocaleDateString('en-GB')}
              {race.isSprint ? ' (Sprint Weekend)' : ' (Full Race)'}
            </option>
          ))}
        </select>
      </div>

      {/* Race Boxes - Stacked */}
      {selectedRace && (
        <div>
          {/* Sprint Race Box - Only show on sprint weekends */}
          {isSprint && (
            <SprintRaceBox
              race={selectedRace}
              drivers={drivers}
              teams={teams}
              predictions={predictions}
              onDriverChange={handleDriverChange}
              onSubmit={handleSubmit}
            />
          )}

          {/* Feature Race Box - Always show */}
          <FeatureRaceBox
            race={selectedRace}
            drivers={drivers}
            teams={teams}
            predictions={predictions}
            onDriverChange={handleDriverChange}
            onSubmit={handleSubmit}
          />
        </div>
      )}

      {!selectedRaceId && (
        <div className="text-center text-gray-400 py-8">
          Select a race to begin making predictions
        </div>
      )}
    </div>
  );
}

export default PredictionForm2026;